import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getAuthorizeUrl } from './entraConfig';

// --- Auth Context ---
interface User {
  email: string;
  name?: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  sessionExpired: boolean;
  login: () => void;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  sessionExpired: false,
  login: () => {},
  logout: async () => {},
  error: null,
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// --- Auth Provider ---
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check existing session on mount
  useEffect(() => {
    async function checkSession() {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/session`, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user || data);
          setIsAuthenticated(true);
        } else if (response.status === 401) {
          setIsAuthenticated(false);
          setUser(null);
        } else {
          setError('Failed to check session');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Service unavailable due to network error',
        );
      } finally {
        setIsLoading(false);
      }
    }

    checkSession();
  }, []);

  // Redirect to Entra ID authorize URL
  const login = useCallback(() => {
    window.location.href = getAuthorizeUrl();
  }, []);

  // Call backend logout endpoint then redirect
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Even if logout request fails, clear local state
    }
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpired(false);
    window.location.href = '/';
  }, []);

  // Mark session as authenticated (called by AuthCallbackPage on success)
  const markAuthenticated = useCallback((userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    setSessionExpired(false);
    setError(null);
  }, []);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    sessionExpired,
    login,
    logout,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      <AuthInternalContext.Provider value={{ markAuthenticated }}>
        {children}
      </AuthInternalContext.Provider>
    </AuthContext.Provider>
  );
}

// Internal context for AuthCallbackPage to mark authentication
interface AuthInternalContextValue {
  markAuthenticated: (user: User) => void;
}

const AuthInternalContext = createContext<AuthInternalContextValue>({
  markAuthenticated: () => {},
});

export function useAuthInternal(): AuthInternalContextValue {
  return useContext(AuthInternalContext);
}
